export enum StepsActions {
  GO_FORWARD = 'GO_FORWARD',
  GO_BACK = 'GO_BACK',
  COMPLETE_STEP = 'COMPLETE_STEP',
  JUMP_TO = 'JUMP_TO',
  SET_STEPS = 'SET_STEPS',
}

type Actions<T = string | number> =
  | { type: typeof StepsActions.GO_FORWARD }
  | { type: typeof StepsActions.GO_BACK }
  | { type: typeof StepsActions.COMPLETE_STEP; payload?: T }
  | { type: typeof StepsActions.JUMP_TO; payload: T }
  | { type: typeof StepsActions.SET_STEPS; payload: T[] };

export const initialStepsState = {
  steps: [],
  step: null,
  stepIndex: 0,
  completed: {},
  nextStep: null,
  previousStep: null,
  isFirstStep: true,
  isLastStep: false,
};

export interface StepperState<T = string | number> {
  steps: T[];
  step: T | null;
  stepIndex: number;
  completed: { [key: string]: boolean };
  nextStep: T | null;
  previousStep: T | null;
  isFirstStep: boolean;
  isLastStep: boolean;
}

export const stepsReducer = <T = string | number,>(
  state: StepperState<T>,
  action: Actions<T>
): StepperState<T> => {
  switch (action.type) {
    case StepsActions.GO_FORWARD: {
      const { stepIndex } = state;
      let nextStepIndex = stepIndex;

      if (stepIndex + 1 < state.steps.length) {
        nextStepIndex = stepIndex + 1;
      }

      return {
        ...state,
        step: state.steps[nextStepIndex],
        stepIndex: nextStepIndex,
        nextStep: state.steps[nextStepIndex + 1] || null,
        previousStep: state.steps[nextStepIndex - 1] || null,
        isFirstStep: nextStepIndex === 0,
        isLastStep: nextStepIndex === state.steps.length - 1,
      };
    }

    case StepsActions.GO_BACK: {
      const { stepIndex } = state;
      let nextStepIndex = stepIndex;

      if (stepIndex - 1 >= 0) {
        nextStepIndex = stepIndex - 1;
      }
      return {
        ...state,
        step: state.steps[nextStepIndex],
        stepIndex: nextStepIndex,
        nextStep: state.steps[nextStepIndex + 1] || null,
        previousStep: state.steps[nextStepIndex - 1] || null,
        isFirstStep: nextStepIndex === 0,
        isLastStep: nextStepIndex === state.steps.length - 1,
      };
    }

    case StepsActions.JUMP_TO: {
      const indexToJump = state.steps.indexOf(action.payload);

      if (indexToJump === -1) {
        return state;
      }

      return {
        ...state,
        step: state.steps[indexToJump],
        stepIndex: indexToJump,
        nextStep: state.steps[indexToJump + 1] || null,
        previousStep: state.steps[indexToJump - 1] || null,
        isFirstStep: indexToJump === 0,
        isLastStep: indexToJump === state.steps.length - 1,
      };
    }

    case StepsActions.SET_STEPS: {
      const { stepIndex } = state;
      const nextSteps = action.payload;
      return {
        ...state,
        step: nextSteps[stepIndex],
        steps: nextSteps,
        nextStep: nextSteps[stepIndex + 1] || null,
        previousStep: nextSteps[stepIndex - 1] || null,
        isFirstStep: stepIndex === 0,
        isLastStep: stepIndex === state.steps.length - 1,
      };
    }

    case StepsActions.COMPLETE_STEP: {
      return {
        ...state,
        completed: {
          ...state.completed,
          [(action.payload || state.steps[state.stepIndex]) as string | number]:
            true,
        },
      };
    }

    default:
      return state;
  }
};
